import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    type SelectChangeEvent
} from '@mui/material'
import React from 'react'
import { Logger } from 'wdc-cube'
import { DateFns } from 'src/utils/date-fns'
import { classToFComponent, FCClassContext, LifesHubConfirmDialog } from 'src/utils/views'
import {
    OVERFLOW_POLICIES,
    type ApiKeyMetadata,
    type ApiKeyStatus,
    type CrmConnection,
    type OverflowPolicy
} from '../../crm_integration_service'
import ProfileCard from './profile-card'
import type { CrmApiKeyForm, CrmApiKeysScope } from '../../gp_scopes'

const LOG = Logger.get('CrmApiKeysSection')

const OVERFLOW_LABEL: Record<OverflowPolicy, string> = {
    always_export: 'Sempre exportar',
    require_authorization: 'Exige autorização',
    deny: 'Nega'
}

const STATUS_COLOR: Record<ApiKeyStatus, 'success' | 'warning' | 'default'> = {
    active: 'success',
    expired: 'warning',
    revoked: 'default'
}

const STATUS_LABEL: Record<ApiKeyStatus, string> = {
    active: 'Ativa',
    expired: 'Expirada',
    revoked: 'Revogada'
}

export type CrmApiKeysSectionProps = {
    className?: string
    scope: CrmApiKeysScope
}

class CrmApiKeysSectionClass implements FCClassContext<CrmApiKeysSectionProps> {
    readonly emitNewClick = () => {
        this.scope.onNew().catch(LOG.caught)
    }

    readonly emitCancelForm = () => {
        this.scope.onCancelForm().catch(LOG.caught)
    }

    readonly emitGenerate = () => {
        this.scope.onGenerate().catch(LOG.caught)
    }

    readonly emitEditClick = (apiKeyId: number) => () => {
        this.scope.onEdit(apiKeyId).catch(LOG.caught)
    }

    readonly emitRevokeClick = (apiKeyId: number) => () => {
        this.scope.onRevoke(apiKeyId).catch(LOG.caught)
    }

    readonly emitCloseGeneratedKey = () => {
        this.scope.onCloseGeneratedKey().catch(LOG.caught)
    }

    readonly emitCopyKey = () => {
        this.scope.onCopyKey().catch(LOG.caught)
    }

    readonly emitSaveEdit = () => {
        this.scope.onSaveEdit().catch(LOG.caught)
    }

    readonly emitCancelEdit = () => {
        this.scope.onCancelEdit().catch(LOG.caught)
    }

    readonly emitConfirmRevoke = () => {
        this.scope.onConfirmRevoke().catch(LOG.caught)
    }

    readonly emitCancelRevoke = () => {
        this.scope.onCancelRevoke().catch(LOG.caught)
    }

    readonly emitFormChange = (patch: Partial<CrmApiKeyForm>) => {
        this.scope.onFormChange(patch).catch(LOG.caught)
    }

    readonly emitFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.emitFormChange({ [e.target.name]: e.target.value } as unknown as Partial<CrmApiKeyForm>)
    }

    readonly emitConnectionChange = (e: SelectChangeEvent<number | string>) => {
        this.emitFormChange({ connectionId: Number(e.target.value) })
    }

    readonly emitOverflowPolicyChange = (e: SelectChangeEvent<OverflowPolicy>) => {
        this.emitFormChange({ overflowPolicy: e.target.value as OverflowPolicy })
    }

    readonly emitEditNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onEditChange({ name: e.target.value }).catch(LOG.caught)
    }

    readonly emitEditOverflowPolicyChange = (e: SelectChangeEvent<OverflowPolicy>) => {
        this.scope.onEditChange({ overflowPolicy: e.target.value as OverflowPolicy }).catch(LOG.caught)
    }

    scope!: CrmApiKeysScope

    onSyncState({ scope }: CrmApiKeysSectionProps) {
        this.scope = scope
    }

    readonly renderConnectionOption = (conn: CrmConnection) => (
        <MenuItem key={conn.id} value={conn.id}>
            {conn.name} ({conn.crm_type})
        </MenuItem>
    )

    readonly renderOverflowPolicyOption = (policy: OverflowPolicy) => (
        <MenuItem key={policy} value={policy}>
            {OVERFLOW_LABEL[policy]}
        </MenuItem>
    )

    readonly renderApiKeyRow = (key: ApiKeyMetadata) => (
        <TableRow key={key.id}>
            <TableCell>{key.connection_name || `#${key.connection_id}`}</TableCell>
            <TableCell>{key.name}</TableCell>
            <TableCell>
                <code>{key.key_prefix}…</code>
            </TableCell>
            <TableCell>
                {key.expires_at ? DateFns.formatDate(new Date(key.expires_at), 'dd/MM/yyyy') : 'Não expira'}
            </TableCell>
            <TableCell>{OVERFLOW_LABEL[key.overflow_policy]}</TableCell>
            <TableCell>
                <Chip size="small" color={STATUS_COLOR[key.status]} label={STATUS_LABEL[key.status]} />
            </TableCell>
            <TableCell align="right">
                <Tooltip title="Editar política / nome">
                    <span>
                        <IconButton
                            color="info"
                            disabled={key.status !== 'active'}
                            onClick={this.emitEditClick(key.id)}
                        >
                            <EditIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Revogar chave">
                    <span>
                        <IconButton
                            color="error"
                            disabled={key.status !== 'active'}
                            onClick={this.emitRevokeClick(key.id)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </TableCell>
        </TableRow>
    )

    render({ className }: CrmApiKeysSectionProps) {
        const scope = this.scope
        const form = scope.form

        return (
            <ProfileCard caption="Integração CRM :: API-keys" className={className}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<AddIcon />}
                        disabled={scope.connections.length === 0}
                        onClick={this.emitNewClick}
                    >
                        Gerar API-key
                    </Button>
                </Box>

                {scope.connections.length === 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Cadastre uma conexão de CRM antes de gerar API-keys.
                    </Alert>
                )}

                {scope.loading && <LinearProgress color="secondary" />}

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Conexão / CRM</TableCell>
                            <TableCell>Nome</TableCell>
                            <TableCell>Prefixo</TableCell>
                            <TableCell>Vencimento</TableCell>
                            <TableCell>Política de excedente</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {scope.apiKeys.length === 0 && !scope.loading && (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary'
                                        }}
                                    >
                                        Nenhuma API-key gerada.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {scope.apiKeys.map(this.renderApiKeyRow)}
                    </TableBody>
                </Table>

                <Dialog open={scope.formOpen} onClose={this.emitCancelForm} maxWidth="sm" fullWidth>
                    <DialogTitle>Gerar nova API-key</DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="apikey-conn-label">Conexão</InputLabel>
                                    <Select
                                        labelId="apikey-conn-label"
                                        label="Conexão"
                                        name="connectionId"
                                        value={form.connectionId ?? ''}
                                        onChange={this.emitConnectionChange}
                                    >
                                        {scope.connections.map(this.renderConnectionOption)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Nome da chave"
                                    name="name"
                                    value={form.name}
                                    onChange={this.emitFieldChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Vencimento (vazio = não expira)"
                                    name="expiresAt"
                                    value={form.expiresAt}
                                    onChange={this.emitFieldChange}
                                    slotProps={{
                                        inputLabel: { shrink: true }
                                    }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="apikey-overflow-label">Política de excedente</InputLabel>
                                    <Select
                                        labelId="apikey-overflow-label"
                                        label="Política de excedente"
                                        name="overflowPolicy"
                                        value={form.overflowPolicy}
                                        onChange={this.emitOverflowPolicyChange}
                                    >
                                        {OVERFLOW_POLICIES.map(this.renderOverflowPolicyOption)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button color="inherit" onClick={this.emitCancelForm}>
                            Cancelar
                        </Button>
                        <Button variant="contained" color="success" disabled={scope.saving} onClick={this.emitGenerate}>
                            Gerar
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={!!scope.generatedKey} onClose={this.emitCloseGeneratedKey} maxWidth="sm" fullWidth>
                    <DialogTitle>Chave gerada</DialogTitle>
                    <DialogContent dividers>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Esta é a <b>única vez</b> que a chave será exibida. Copie e guarde em local seguro.
                        </Alert>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                fullWidth
                                value={scope.generatedKey?.apiKey ?? ''}
                                multiline
                                slotProps={{
                                    input: { readOnly: true }
                                }}
                            />
                            <Tooltip title="Copiar">
                                <IconButton color="primary" onClick={this.emitCopyKey}>
                                    <ContentCopyIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        {scope.copied && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'success.main'
                                }}
                            >
                                Copiada!
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" onClick={this.emitCloseGeneratedKey}>
                            Fechar
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={!!scope.editing} onClose={this.emitCancelEdit} maxWidth="sm" fullWidth>
                    <DialogTitle>Editar API-key</DialogTitle>
                    <DialogContent dividers>
                        <DialogContentText sx={{ mb: 2 }}>
                            {scope.editing?.name} — <code>{scope.editing?.key_prefix}…</code>
                        </DialogContentText>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Nome da chave"
                                    value={scope.editName}
                                    onChange={this.emitEditNameChange}
                                />
                            </Grid>
                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="apikey-edit-overflow-label">Política de excedente</InputLabel>
                                    <Select
                                        labelId="apikey-edit-overflow-label"
                                        label="Política de excedente"
                                        value={scope.editOverflowPolicy}
                                        onChange={this.emitEditOverflowPolicyChange}
                                    >
                                        {OVERFLOW_POLICIES.map(this.renderOverflowPolicyOption)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button color="inherit" onClick={this.emitCancelEdit}>
                            Cancelar
                        </Button>
                        <Button variant="contained" color="success" onClick={this.emitSaveEdit}>
                            Salvar
                        </Button>
                    </DialogActions>
                </Dialog>

                <LifesHubConfirmDialog
                    open={!!scope.revokeTarget}
                    title="Revogar chave"
                    subtitle={scope.revokeTarget?.name}
                    tone="danger"
                    message="A chave deixa de autenticar imediatamente."
                    confirmLabel="Revogar chave"
                    onConfirm={this.emitConfirmRevoke}
                    onCancel={this.emitCancelRevoke}
                />
            </ProfileCard>
        )
    }
}

export const CrmApiKeysSection = classToFComponent(CrmApiKeysSectionClass, React)
export default CrmApiKeysSection
